import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import LoadingSpinner from './shared/LoadingSpinner';
import {
  fetchUserRole,
  resolveAdminPanelAccess,
  signOutAdminFirebase,
} from '../services/adminAuthService';
import { auth } from '../firebase';

const ProtectedAdminRoute = ({ children }) => {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [sessionValid, setSessionValid] = useState(false);
  const [usherOnly, setUsherOnly] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (cancelled) return;

      if (!user) {
        setSessionValid(false);
        setUsherOnly(false);
        setAccessDenied(false);
        setChecking(false);
        return;
      }

      try {
        const role = await fetchUserRole(user.uid);
        const access = resolveAdminPanelAccess(role);

        if (access === 'admin') {
          setSessionValid(true);
          setUsherOnly(false);
          setAccessDenied(false);
        } else if (access === 'usher') {
          setSessionValid(false);
          setUsherOnly(true);
          setAccessDenied(false);
        } else {
          await signOutAdminFirebase();
          setSessionValid(false);
          setUsherOnly(false);
          setAccessDenied(true);
        }
      } catch (error) {
        console.error('Failed to verify admin access:', error);
        setSessionValid(false);
        setUsherOnly(false);
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  if (checking) {
    return <LoadingSpinner message="Verifying admin access..." />;
  }

  if (usherOnly) {
    return <Navigate to="/conference-scan" replace />;
  }

  if (!sessionValid) {
    return (
      <Navigate
        to="/admin/login"
        state={{ from: location, denied: accessDenied || undefined }}
        replace
      />
    );
  }

  return children;
};

export default ProtectedAdminRoute;

export const logoutAdmin = async () => {
  try {
    await signOutAdminFirebase();
  } catch (error) {
    console.warn('Admin Firebase sign-out failed:', error);
  }

  window.location.href = '/admin/login';
};
